import React, { useState, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { MenuItem, MenuCategory, DietaryFlag } from '../types';
import { apiService } from '../services/apiService';
import { formatEuro } from '../utils/format';
import { FoodTypeBadge } from './FoodTypeBadge';
import {
  UtensilsCrossed,
  Plus,
  Edit2,
  Trash2,
  Check,
  X,
  Sparkles,
  Flame,
  Eye,
  EyeOff,
  Upload,
  Image as ImageIcon,
  FolderTree,
  Percent,
  Search,
  Sliders,
  DollarSign,
  AlertCircle,
  Wine,
  Star
} from 'lucide-react';

export const MenuManagement: React.FC = () => {
  const { categories, refreshMenu, currentView } = useApp();

  const activeSubTab =
    currentView === 'staff-menu-categories'
      ? 'categories'
      : currentView === 'staff-menu-taxes'
      ? 'taxes'
      : 'dishes';

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('all');

  // Dish Modal States
  const [isEditingItem, setIsEditingItem] = useState<MenuItem | null>(null);
  const [isDishModalOpen, setIsDishModalOpen] = useState(false);

  // Form states for creating/editing item
  const [formCategory, setFormCategory] = useState(categories[0]?.id || 'cat_pizzas');
  const [nameEn, setNameEn] = useState('');
  const [nameFi, setNameFi] = useState('');
  const [nameSv, setNameSv] = useState('');
  const [descEn, setDescEn] = useState('');
  const [descFi, setDescFi] = useState('');
  const [descSv, setDescSv] = useState('');
  const [price, setPrice] = useState('16.50');
  const [vatRate, setVatRate] = useState<number>(14.0);
  const [imageUrl, setImageUrl] = useState('');
  const [dietaryFlags, setDietaryFlags] = useState<DietaryFlag[]>(['L']);
  const [isPopular, setIsPopular] = useState(false);
  const [isRecommended, setIsRecommended] = useState(false);
  const [isAlcohol, setIsAlcohol] = useState(false);
  const [alcoholAbv, setAlcoholAbv] = useState<string>('5.0');

  // File upload input ref
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Category Modal States
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<MenuCategory | null>(null);
  const [catNameEn, setCatNameEn] = useState('');
  const [catNameFi, setCatNameFi] = useState('');
  const [catNameSv, setCatNameSv] = useState('');
  const [catDesc, setCatDesc] = useState('');
  const [catIcon, setCatIcon] = useState('🍕');
  const [catSortOrder, setCatSortOrder] = useState('1');

  // Tax settings state
  const [globalFoodVat, setGlobalFoodVat] = useState<number>(14.0);
  const [globalBevVat, setGlobalBevVat] = useState<number>(25.5);
  const [isSavingTaxes, setIsSavingTaxes] = useState(false);

  // Handle local image file upload and convert to base64
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('File size exceeds 5MB. Please choose a smaller image.');
      return;
    }

    const reader = new FileReader();
    reader.onload = event => {
      if (event.target?.result) {
        setImageUrl(event.target.result as string);
      }
    };
    reader.readAsDataURL(file);
  };

  const openNewDishModal = () => {
    setIsEditingItem(null);
    setFormCategory(categories[0]?.id || 'cat_pizzas');
    setNameEn('');
    setNameFi('');
    setNameSv('');
    setDescEn('');
    setDescFi('');
    setDescSv('');
    setPrice('17.50');
    setVatRate(14.0);
    setImageUrl('https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?auto=format&fit=crop&w=800&q=80');
    setDietaryFlags(['L']);
    setIsPopular(false);
    setIsRecommended(false);
    setIsAlcohol(false);
    setAlcoholAbv('5.0');
    setIsDishModalOpen(true);
  };

  const handleEditDish = (item: MenuItem) => {
    setIsEditingItem(item);
    setFormCategory(item.category_id);
    setNameEn(item.name_en || item.name_fi);
    setNameFi(item.name_fi);
    setNameSv(item.name_sv || '');
    setDescEn(item.description_en || item.description_fi);
    setDescFi(item.description_fi);
    setDescSv(item.description_sv || '');
    setPrice(item.price.toString());
    setVatRate(item.vat_rate);
    setImageUrl(item.image_url);
    setDietaryFlags(item.dietary_flags || []);
    setIsPopular(Boolean(item.is_popular));
    setIsRecommended(Boolean(item.is_recommended));
    setIsAlcohol(Boolean(item.is_alcohol));
    setAlcoholAbv(item.alcohol_abv ? item.alcohol_abv.toString() : '5.0');
    setIsDishModalOpen(true);
  };

  const handleDeleteDish = async (itemId: string) => {
    if (!window.confirm('Are you sure you want to delete this dish from the menu?')) return;

    await apiService.deleteMenuItem(itemId);
    await refreshMenu();
  };

  const toggleDietaryFlag = (flag: DietaryFlag) => {
    if (dietaryFlags.includes(flag)) {
      setDietaryFlags(dietaryFlags.filter(f => f !== flag));
    } else {
      setDietaryFlags([...dietaryFlags, flag]);
    }
  };

  const handleSaveDish = async (e: React.FormEvent) => {
    e.preventDefault();
    const numericPrice = parseFloat(price.replace(',', '.')) || 15.0;

    const newItem: MenuItem = {
      id: isEditingItem ? isEditingItem.id : `item_${Date.now()}`,
      category_id: formCategory,
      name_en: nameEn.trim() || nameFi.trim() || 'New Item',
      name_fi: nameFi.trim() || nameEn.trim() || 'Uusi Annos',
      name_sv: nameSv.trim() || nameEn.trim() || 'Ny Rätt',
      description_en: descEn.trim() || 'Crafted with premium fresh ingredients.',
      description_fi: descFi.trim() || 'Valmistettu tuoreista laadukkaista raaka-aineista.',
      description_sv: descSv.trim() || 'Tillagad med färska råvaror.',
      price: numericPrice,
      vat_rate: Number(vatRate),
      image_url: imageUrl.trim() || 'https://images.unsplash.com/photo-1513104890138-7c749659a591?auto=format&fit=crop&w=800&q=80',
      is_available: isEditingItem ? isEditingItem.is_available : true,
      is_popular: isPopular,
      is_recommended: isRecommended,
      is_alcohol: isAlcohol,
      alcohol_abv: isAlcohol ? parseFloat(alcoholAbv) || 5.0 : undefined,
      spice_level: 0,
      dietary_flags: dietaryFlags,
      sort_order: isEditingItem ? isEditingItem.sort_order : 99,
      variants: isEditingItem?.variants
    };

    await apiService.saveMenuItem(newItem);
    await refreshMenu();
    setIsDishModalOpen(false);
  };

  // Category Actions
  const openNewCategoryModal = () => {
    setEditingCategory(null);
    setCatNameEn('');
    setCatNameFi('');
    setCatNameSv('');
    setCatDesc('');
    setCatIcon('🍕');
    setCatSortOrder((categories.length + 1).toString());
    setIsCategoryModalOpen(true);
  };

  const handleEditCategory = (cat: MenuCategory) => {
    setEditingCategory(cat);
    setCatNameEn(cat.name_en || cat.name_fi);
    setCatNameFi(cat.name_fi);
    setCatNameSv(cat.name_sv || '');
    setCatDesc(cat.description || '');
    setCatIcon(cat.icon || '🍕');
    setCatSortOrder(cat.sort_order.toString());
    setIsCategoryModalOpen(true);
  };

  const handleDeleteCategory = async (catId: string) => {
    const cat = categories.find(c => c.id === catId);
    if ((cat?.items?.length || 0) > 0) {
      alert(`Cannot delete "${cat?.name_en || cat?.name_fi}" because it still contains ${cat?.items?.length} items. Move or delete the items first.`);
      return;
    }
    if (!window.confirm(`Are you sure you want to delete category "${cat?.name_en || cat?.name_fi}"?`)) return;

    await apiService.deleteCategory(catId);
    await refreshMenu();
  };

  const handleSaveCategory = async (e: React.FormEvent) => {
    e.preventDefault();

    const newCat: MenuCategory = {
      id: editingCategory ? editingCategory.id : `cat_${Date.now()}`,
      name_en: catNameEn.trim() || catNameFi.trim() || 'New Category',
      name_fi: catNameFi.trim() || catNameEn.trim() || 'Uusi Kategoria',
      name_sv: catNameSv.trim() || catNameEn.trim() || 'Ny Kategori',
      description: catDesc.trim(),
      icon: catIcon.trim() || '🍕',
      sort_order: parseInt(catSortOrder) || 1,
      is_active: editingCategory ? editingCategory.is_active : true,
      items: editingCategory ? editingCategory.items : []
    };

    await apiService.saveCategory(newCat);
    await refreshMenu();
    setIsCategoryModalOpen(false);
  };

  // Tax Save Action
  const handleApplyGlobalTaxes = async () => {
    setIsSavingTaxes(true);
    const updated = categories.map(cat => ({
      ...cat,
      items: (cat.items || []).map(item => ({
        ...item,
        vat_rate: item.is_alcohol || cat.id.includes('bev') || cat.id.includes('alcohol') ? globalBevVat : globalFoodVat
      }))
    }));

    localStorage.setItem('ovenstory_categories_v2', JSON.stringify(updated));
    await refreshMenu();
    setIsSavingTaxes(false);
    alert('Tax rates successfully updated and applied across all menu items!');
  };

  const handleToggleAvailability = async (itemId: string, currentAvailable: boolean) => {
    const updated = categories.map(cat => ({
      ...cat,
      items: (cat.items || []).map(item =>
        item.id === itemId ? { ...item, is_available: !currentAvailable } : item
      )
    }));
    localStorage.setItem('ovenstory_categories_v2', JSON.stringify(updated));
    await refreshMenu();
  };

  // Filtered dishes for display
  const allDishes = categories.flatMap(cat =>
    (cat.items || []).map(item => ({ ...item, categoryName: cat.name_en || cat.name_fi }))
  );

  const filteredDishes = allDishes.filter((item: any) => {
    const matchesCat = selectedCategoryFilter === 'all' || item.category_id === selectedCategoryFilter;
    const q = searchQuery.toLowerCase().trim();
    const matchesSearch =
      !q ||
      item.name_en.toLowerCase().includes(q) ||
      item.name_fi.toLowerCase().includes(q) ||
      item.description_en.toLowerCase().includes(q);
    return matchesCat && matchesSearch;
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Dynamic Page Header based on Sidebar Selection */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
          background: 'var(--bg-secondary)',
          padding: '16px 20px',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-subtle)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '38px',
              height: '38px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--accent-copper-light)',
              color: 'var(--accent-copper)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {activeSubTab === 'dishes' && <UtensilsCrossed size={20} />}
            {activeSubTab === 'categories' && <FolderTree size={20} />}
            {activeSubTab === 'taxes' && <Percent size={20} />}
          </div>
          <div>
            <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.25rem', fontWeight: 800 }}>
              {activeSubTab === 'dishes' && `Food Items & Dishes (${allDishes.length})`}
              {activeSubTab === 'categories' && `Menu Categories (${categories.length})`}
              {activeSubTab === 'taxes' && 'Tax & ALV Configuration'}
            </h2>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              {activeSubTab === 'dishes' && 'Manage dish photos, pricing, availability, and Finnish translations'}
              {activeSubTab === 'categories' && 'Add, reorder, and configure restaurant menu categories'}
              {activeSubTab === 'taxes' && 'Configure statutory Finnish ALV rates (14% Food / 25.5% Alcohol)'}
            </div>
          </div>
        </div>

        {activeSubTab === 'dishes' && (
          <button className="btn-primary" onClick={openNewDishModal}>
            <Plus size={16} />
            <span>Add New Dish</span>
          </button>
        )}

        {activeSubTab === 'categories' && (
          <button className="btn-primary" onClick={openNewCategoryModal}>
            <Plus size={16} />
            <span>Add New Category</span>
          </button>
        )}
      </div>

      {/* SUB-TAB 1: DISHES & ITEMS LISTING */}
      {activeSubTab === 'dishes' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Filter & Search Bar */}
          <div
            style={{
              display: 'flex',
              gap: '12px',
              flexWrap: 'wrap',
              alignItems: 'center',
              background: 'var(--bg-secondary)',
              padding: '12px 16px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-subtle)'
            }}
          >
            <div style={{ flex: 1, minWidth: '200px', display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--bg-card)', padding: '8px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)' }}>
              <Search size={16} color="var(--text-muted)" />
              <input
                type="text"
                placeholder="Search dish by English or Finnish name..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-primary)', outline: 'none', width: '100%', fontSize: '0.88rem' }}
              />
            </div>

            <select
              className="form-input"
              style={{ width: 'auto', minWidth: '180px' }}
              value={selectedCategoryFilter}
              onChange={e => setSelectedCategoryFilter(e.target.value)}
            >
              <option value="all">All Categories ({allDishes.length})</option>
              {categories.map(c => (
                <option key={c.id} value={c.id}>
                  {c.icon} {c.name_en || c.name_fi} ({c.items?.length || 0})
                </option>
              ))}
            </select>
          </div>

          {/* Dishes Table */}
          <div
            style={{
              background: 'var(--bg-card)',
              borderRadius: 'var(--radius-xl)',
              border: '1px solid var(--border-subtle)',
              overflowX: 'auto',
              boxShadow: 'var(--shadow-sm)'
            }}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-subtle)', fontSize: '0.78rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <th style={{ padding: '12px 16px' }}>Photo</th>
                  <th style={{ padding: '12px 16px' }}>Dish Name</th>
                  <th style={{ padding: '12px 16px' }}>Category</th>
                  <th style={{ padding: '12px 16px' }}>Price & Tax</th>
                  <th style={{ padding: '12px 16px' }}>Dietary & Flags</th>
                  <th style={{ padding: '12px 16px' }}>Status</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDishes.map(item => (
                  <tr
                    key={item.id}
                    style={{
                      borderBottom: '1px solid var(--border-subtle)',
                      opacity: item.is_available ? 1 : 0.6,
                      transition: 'background var(--transition-fast)'
                    }}
                  >
                    <td style={{ padding: '12px 16px', width: '60px' }}>
                      <img
                        src={item.image_url}
                        alt={item.name_en}
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: 'var(--radius-md)',
                          objectFit: 'cover',
                          border: '1px solid var(--border-subtle)'
                        }}
                      />
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ fontWeight: 800, fontSize: '0.92rem', color: 'var(--text-primary)' }}>
                        {item.name_en || item.name_fi}
                      </div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                        {item.name_fi}
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '0.84rem' }}>
                      <span style={{ background: 'var(--bg-secondary)', padding: '3px 8px', borderRadius: 'var(--radius-xs)', border: '1px solid var(--border-subtle)' }}>
                        {item.categoryName}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ fontWeight: 800, fontSize: '0.92rem', color: 'var(--accent-copper)' }}>
                        {formatEuro(item.price)}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                        ALV: {item.vat_rate}%
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
                        {item.dietary_flags.includes('VEG') && <FoodTypeBadge type="vegan" size={15} />}
                        {item.dietary_flags.includes('G') && <FoodTypeBadge type="gluten-free" size={15} />}
                        {item.dietary_flags.includes('L') && <FoodTypeBadge type="lactose-free" size={15} />}
                        {item.dietary_flags.includes('M') && <FoodTypeBadge type="dairy-free" size={15} />}
                        {item.is_alcohol && (
                          <span style={{ fontSize: '0.68rem', background: 'rgba(244,63,94,0.1)', color: '#f43f5e', padding: '1px 5px', borderRadius: '3px', fontWeight: 800 }}>
                            18+ ({item.alcohol_abv}%)
                          </span>
                        )}
                        {item.is_popular && (
                          <span style={{ fontSize: '0.68rem', background: 'rgba(245,158,11,0.1)', color: '#f59e0b', padding: '1px 5px', borderRadius: '3px', fontWeight: 800 }}>
                            ★ Bestseller
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <button
                        onClick={() => handleToggleAvailability(item.id, item.is_available)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 10px',
                          borderRadius: 'var(--radius-full)',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          cursor: 'pointer',
                          background: item.is_available ? 'rgba(16, 185, 129, 0.1)' : 'rgba(225, 29, 72, 0.1)',
                          color: item.is_available ? 'var(--accent-emerald)' : '#e11d48',
                          border: `1px solid ${item.is_available ? 'rgba(16, 185, 129, 0.3)' : 'rgba(225, 29, 72, 0.3)'}`
                        }}
                      >
                        {item.is_available ? <Eye size={12} /> : <EyeOff size={12} />}
                        <span>{item.is_available ? 'Available' : 'Sold Out'}</span>
                      </button>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: '8px' }}>
                        <button
                          className="btn-secondary"
                          style={{ padding: '6px 10px', fontSize: '0.78rem' }}
                          onClick={() => handleEditDish(item)}
                          title="Edit Dish"
                        >
                          <Edit2 size={13} />
                          <span>Edit</span>
                        </button>
                        <button
                          className="btn-secondary"
                          style={{ padding: '6px 10px', fontSize: '0.78rem', color: '#e11d48' }}
                          onClick={() => handleDeleteDish(item.id)}
                          title="Delete Dish"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* SUB-TAB 2: CATEGORIES STUDIO */}
      {activeSubTab === 'categories' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '14px' }}>
            {categories.map(cat => (
              <div
                key={cat.id}
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-lg)',
                  padding: '16px 20px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '12px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div
                    style={{
                      fontSize: '1.6rem',
                      width: '44px',
                      height: '44px',
                      borderRadius: 'var(--radius-md)',
                      background: 'var(--bg-secondary)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    {cat.icon || '🍕'}
                  </div>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: '0.96rem', color: 'var(--text-primary)' }}>
                      {cat.name_en || cat.name_fi}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                      FI: {cat.name_fi} • Order: #{cat.sort_order} • Items: {cat.items?.length || 0}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '6px' }}>
                  <button
                    className="btn-secondary"
                    style={{ padding: '6px 10px', fontSize: '0.78rem' }}
                    onClick={() => handleEditCategory(cat)}
                  >
                    <Edit2 size={13} />
                  </button>
                  <button
                    className="btn-secondary"
                    style={{ padding: '6px 10px', fontSize: '0.78rem', color: '#e11d48' }}
                    onClick={() => handleDeleteCategory(cat.id)}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SUB-TAB 3: TAX & ALV SETTINGS */}
      {activeSubTab === 'taxes' && (
        <div
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-xl)',
            padding: '24px',
            maxWidth: '680px',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
          }}
        >
          <div>
            <h3 style={{ fontSize: '1.2rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Percent size={20} color="var(--accent-copper)" />
              <span>Finnish ALV Tax Configuration (Vero)</span>
            </h3>
            <p style={{ fontSize: '0.84rem', color: 'var(--text-muted)', marginTop: '4px' }}>
              Configure statutory Finnish Value-Added Tax rates. Changes apply directly to customer receipts and tax breakdown calculations.
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div className="form-group">
              <label className="form-label" style={{ fontWeight: 800 }}>
                1. Standard Food & Non-Alcoholic Beverage VAT Rate (%):
              </label>
              <input
                type="number"
                step="0.1"
                className="form-input"
                value={globalFoodVat}
                onChange={e => setGlobalFoodVat(parseFloat(e.target.value) || 14.0)}
              />
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                Standard Finnish food VAT is <strong>14.0%</strong> (Ruuan ja alkoholittomien juomien arvonlisävero).
              </span>
            </div>

            <div className="form-group">
              <label className="form-label" style={{ fontWeight: 800 }}>
                2. Alcohol & Standard Goods VAT Rate (%):
              </label>
              <input
                type="number"
                step="0.1"
                className="form-input"
                value={globalBevVat}
                onChange={e => setGlobalBevVat(parseFloat(e.target.value) || 25.5)}
              />
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                Finnish general VAT for alcoholic beverages and merchandise is <strong>25.5%</strong> (effective from Sep 2024).
              </span>
            </div>

            <button
              className="btn-primary"
              style={{ marginTop: '10px' }}
              onClick={handleApplyGlobalTaxes}
              disabled={isSavingTaxes}
            >
              <Check size={16} />
              <span>{isSavingTaxes ? 'Applying Tax Rates...' : 'Apply Tax Rates to All Menu Items'}</span>
            </button>
          </div>
        </div>
      )}

      {/* DISH CREATE / EDIT MODAL */}
      {isDishModalOpen && (
        <div className="modal-overlay" onClick={() => setIsDishModalOpen(false)}>
          <div
            className="modal-card"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '640px', maxHeight: '90vh', overflowY: 'auto' }}
          >
            <div className="modal-header">
              <div className="modal-title">
                {isEditingItem ? `Edit Dish: ${nameEn || nameFi}` : 'Create New Menu Dish'}
              </div>
              <button className="modal-close-btn" onClick={() => setIsDishModalOpen(false)}>
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSaveDish} style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Category selector */}
              <div className="form-group">
                <label className="form-label" style={{ fontWeight: 800 }}>Category</label>
                <select
                  className="form-input"
                  value={formCategory}
                  onChange={e => setFormCategory(e.target.value)}
                  required
                >
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.icon} {c.name_en || c.name_fi}
                    </option>
                  ))}
                </select>
              </div>

              {/* Names in EN, FI, SV */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: 800 }}>Name (English) *</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. Smoked Salmon Pizza"
                    value={nameEn}
                    onChange={e => setNameEn(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Name (Finnish)</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. Kylmäsavulohi Pizza"
                    value={nameFi}
                    onChange={e => setNameFi(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Name (Swedish)</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. Rökt Lax Pizza"
                    value={nameSv}
                    onChange={e => setNameSv(e.target.value)}
                  />
                </div>
              </div>

              {/* Price and VAT */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: 800 }}>Price (€ EUR) *</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="17.50"
                    value={price}
                    onChange={e => setPrice(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: 800 }}>VAT Rate (%)</label>
                  <select
                    className="form-input"
                    value={vatRate}
                    onChange={e => setVatRate(Number(e.target.value))}
                  >
                    <option value={14.0}>14.0% (Standard Food & Soft Drinks)</option>
                    <option value={25.5}>25.5% (Alcoholic Beverages 18+)</option>
                    <option value={0.0}>0.0% (Zero Tax Exempt)</option>
                  </select>
                </div>
              </div>

              {/* Descriptions */}
              <div className="form-group">
                <label className="form-label">Description (English)</label>
                <textarea
                  className="form-input"
                  rows={2}
                  placeholder="Ingredients and culinary notes..."
                  value={descEn}
                  onChange={e => setDescEn(e.target.value)}
                />
              </div>

              {/* PHOTO UPLOAD & URL SECTION */}
              <div
                style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-lg)',
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}
              >
                <label className="form-label" style={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <ImageIcon size={16} color="var(--accent-copper)" />
                  <span>Dish Image (File Upload or URL)</span>
                </label>

                <div style={{ display: 'flex', gap: '14px', alignItems: 'center', flexWrap: 'wrap' }}>
                  {imageUrl && (
                    <img
                      src={imageUrl}
                      alt="Preview"
                      style={{
                        width: '70px',
                        height: '70px',
                        borderRadius: 'var(--radius-md)',
                        objectFit: 'cover',
                        border: '2px solid var(--accent-copper)',
                        flexShrink: 0
                      }}
                    />
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, minWidth: '200px' }}>
                    {/* Hidden file input */}
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleFileUpload}
                    />

                    <button
                      type="button"
                      className="btn-secondary"
                      style={{ width: 'fit-content', gap: '8px' }}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload size={14} />
                      <span>Upload Image File from Device</span>
                    </button>

                    <input
                      type="url"
                      className="form-input"
                      placeholder="Or paste direct image URL (https://...)"
                      value={imageUrl}
                      onChange={e => setImageUrl(e.target.value)}
                      style={{ fontSize: '0.82rem' }}
                    />
                  </div>
                </div>
              </div>

              {/* Dietary Flags & Alcohol */}
              <div className="form-group">
                <label className="form-label" style={{ fontWeight: 800 }}>Dietary Classifications</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {[
                    { id: 'VEG', label: 'Vegan (🌱)', type: 'vegan' as const },
                    { id: 'G', label: 'Gluten-Free (🌾)', type: 'gluten-free' as const },
                    { id: 'L', label: 'Lactose-Free (🥛)', type: 'lactose-free' as const },
                    { id: 'M', label: 'Dairy-Free / Maidoton (🥥)', type: 'dairy-free' as const },
                    { id: 'VL', label: 'Low Lactose (VL)', type: 'lactose-free' as const }
                  ].map(f => {
                    const isChecked = dietaryFlags.includes(f.id as DietaryFlag);
                    return (
                      <button
                        key={f.id}
                        type="button"
                        onClick={() => toggleDietaryFlag(f.id as DietaryFlag)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 12px',
                          borderRadius: 'var(--radius-md)',
                          fontSize: '0.8rem',
                          fontWeight: 700,
                          cursor: 'pointer',
                          background: isChecked ? 'var(--accent-copper)' : 'var(--bg-surface)',
                          color: isChecked ? '#fff' : 'var(--text-secondary)',
                          border: `1px solid ${isChecked ? 'var(--accent-copper)' : 'var(--border-subtle)'}`
                        }}
                      >
                        <FoodTypeBadge type={f.type} size={14} showTooltip={false} />
                        <span>{f.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Alcohol & Bestseller checkboxes */}
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.86rem', fontWeight: 700 }}>
                  <input
                    type="checkbox"
                    checked={isAlcohol}
                    onChange={e => {
                      setIsAlcohol(e.target.checked);
                      if (e.target.checked) setVatRate(25.5);
                    }}
                  />
                  <span>Alcoholic Beverage (18+)</span>
                </label>

                {isAlcohol && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>ABV %:</span>
                    <input
                      type="number"
                      step="0.1"
                      className="form-input"
                      style={{ width: '70px', padding: '4px 8px' }}
                      value={alcoholAbv}
                      onChange={e => setAlcoholAbv(e.target.value)}
                    />
                  </div>
                )}

                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.86rem', fontWeight: 700 }}>
                  <input
                    type="checkbox"
                    checked={isPopular}
                    onChange={e => setIsPopular(e.target.checked)}
                  />
                  <span>★ Bestseller Dish</span>
                </label>
              </div>

              <button type="submit" className="btn-primary" style={{ marginTop: '8px' }}>
                <Check size={16} />
                <span>Save Dish</span>
              </button>
            </form>
          </div>
        </div>
      )}

      {/* CATEGORY CREATE / EDIT MODAL */}
      {isCategoryModalOpen && (
        <div className="modal-overlay" onClick={() => setIsCategoryModalOpen(false)}>
          <div
            className="modal-card"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '480px' }}
          >
            <div className="modal-header">
              <div className="modal-title">
                {editingCategory ? 'Edit Category' : 'Create New Category'}
              </div>
              <button className="modal-close-btn" onClick={() => setIsCategoryModalOpen(false)}>
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSaveCategory} style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div className="form-group">
                <label className="form-label" style={{ fontWeight: 800 }}>Category Name (English) *</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. Wood-Fired Pizzas"
                  value={catNameEn}
                  onChange={e => setCatNameEn(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Category Name (Finnish)</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. Puu-uunipizzat"
                  value={catNameFi}
                  onChange={e => setCatNameFi(e.target.value)}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: 800 }}>Icon Emoji</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="🍕"
                    value={catIcon}
                    onChange={e => setCatIcon(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: 800 }}>Sort Order</label>
                  <input
                    type="number"
                    className="form-input"
                    value={catSortOrder}
                    onChange={e => setCatSortOrder(e.target.value)}
                  />
                </div>
              </div>

              <button type="submit" className="btn-primary" style={{ marginTop: '8px' }}>
                <Check size={16} />
                <span>Save Category</span>
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
